import { useEffect, useState } from "react";
import { Target, Save, RadarIcon } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, PolarRadiusAxis } from "recharts";
import { api } from "../lib/apiClient";
import { useApp } from "../lib/store";
import { PageHeader, GradeBadge, Avatar, ScoreBar } from "../components/Shared";
import { useEmployees, EmployeePicker, Panel } from "../components/Filters";
import { toast } from "sonner";

export default function KRA() {
  const { month, year, can, isEmployee, selfEmployeeId } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "om");
  const [data, setData] = useState(null);

  const load = () => api.monthlyReview(empId, month, year).then(setData).catch(() => {});
  useEffect(() => { if (empId) load(); }, [empId, month, year]);

  const updateType = (pid, type) => {
    if (!can.editKra) return;
    const emp = data.employee;
    const params = emp.kra_params.map((p) => p.param_id === pid ? { ...p, type } : p);
    api.updateEmployee(emp.id, { kra_params: params }).then(() => { toast.success("KRA parameter updated"); load(); });
  };

  if (!data) return <div className="py-20 text-center text-muted-c">Loading…</div>;
  const emp = data.employee, review = data.review;
  const radarData = review.kra_scores.map((k) => ({ param: k.name.split(" ").slice(0, 2).join(" "), score: k.final_score }));

  return (
    <div>
      <PageHeader title="KRA Framework" subtitle="10 parameters · max 10 marks each · total 100"
        exportModule="kra" exportParams={{ employee_id: empId, month, year }}>
        <EmployeePicker value={empId} onChange={setEmpId} employees={employees} disabled={isEmployee} testid="kra-employee-picker" />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-4">
          <Panel testid="kra-summary">
            <div className="flex items-center gap-3 mb-4">
              <Avatar src={emp.profile_image} name={emp.name} size={52} />
              <div>
                <p className="font-display font-bold text-lg">{emp.name}</p>
                <p className="text-sm text-secondary">{emp.designation}</p>
              </div>
            </div>
            <div className="elevated rounded-xl p-4 text-center mb-3">
              <p className="text-xs uppercase tracking-wider text-muted-c mb-1">Total KRA Score</p>
              <p className="mono font-bold text-4xl">{review.total_score}<span className="text-lg text-muted-c">/100</span></p>
              <div className="mt-2"><GradeBadge grade={review.grade} size="lg" /></div>
            </div>
            <div className="text-sm text-secondary flex justify-between py-1.5 border-b border-subtle"><span>Auto-scored params</span><span className="mono font-semibold">{emp.kra_params.filter((p) => p.type === "auto").length}</span></div>
            <div className="text-sm text-secondary flex justify-between py-1.5"><span>Manual params</span><span className="mono font-semibold">{emp.kra_params.filter((p) => p.type === "manual").length}</span></div>
          </Panel>
        </div>
        <div className="lg:col-span-8">
          <Panel title="10-KRA Competency Radar" testid="kra-radar">
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData} outerRadius={110}>
                <PolarGrid stroke="var(--border-subtle)" />
                <PolarAngleAxis dataKey="param" tick={{ fill: "#64748B", fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} />
                <Radar dataKey="score" stroke="#2563EB" fill="#2563EB" fillOpacity={0.3} strokeWidth={2} isAnimationActive={false} />
              </RadarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="kra-table">
        <table className="data-table">
          <thead><tr><th>#</th><th>KRA Parameter</th><th>Type</th><th>Score</th><th>Progress</th></tr></thead>
          <tbody>
            {review.kra_scores.map((k) => (
              <tr key={k.param_id}>
                <td className="mono text-muted-c">{k.param_id}</td>
                <td className="font-medium">{k.name}</td>
                <td>
                  {can.editKra ? (
                    <select value={k.type} onChange={(e) => updateType(k.param_id, e.target.value)} className="input-c py-1 text-xs" style={{ width: 110 }} data-testid={`kra-type-${k.param_id}`}>
                      <option value="auto">Automatic</option>
                      <option value="manual">Manual</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  ) : (
                    <span className={`pill px-2.5 py-0.5 text-xs ${k.type === "auto" ? "brand-text" : "text-secondary"} border-subtle`}>{k.type}</span>
                  )}
                </td>
                <td className="mono font-bold">{k.final_score?.toFixed(1)}<span className="text-muted-c text-xs">/10</span></td>
                <td style={{ minWidth: 160 }}><ScoreBar value={k.final_score} max={10} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold"><td></td><td>TOTAL</td><td></td><td className="mono">{review.total_score}/100</td><td><GradeBadge grade={review.grade} /></td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
